import React from "react";
import { useHistory } from "react-router-dom";

import { Grid, Typography } from "@material-ui/core";


import {
  ArrayField,
  Datagrid,
  FunctionField,
  Labeled,
  Loading,
  NumberField,
  SelectField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useRefresh,
  useShowController,
} from "react-admin";

import { BackButton } from "../../../components/common/BackButton";
import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HistoryTab } from "../../../components/common/HistoryTab";
import { NotesTabShow } from "../../../components/common/NotesTab";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import { ShowButton } from "../../../components/common/ShowButton";
import { InventoryStatus, LotTypes } from "../../../utils/constants";
import {
  getLocationFullName,
  getAreaName,
  getZoneName,
} from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  deepRedirect,
  exceptionRenderer,
  getBackPath,
  quantityRenderer,
  trimDecimal,
} from "../../../utils/utils";
import {
  shouldRefreshInventoryTrackCar,
  useTrackCarSubscriptionRefresh,
} from "../../../utils/useTrackCarSubscriptionRefresh";
import { AuditStatusIcon } from "./AuditStatusIcon";
import { ValidateRequiredIcon } from "./ValidateRequiredIcon";
import { InventoryAside } from "./InventoryAside";
import { showWeight } from "./InventoryUitls";

const InventorySetupTab = (props) => {
  const { history, backPath, record } = props;
  const yardCommodities =
    window.appUser.reloadYard.activeYardCommodities.items || [];
  const inventoryItems = {
    items:
      record?.inventoryItems?.items?.sort((a, b) => {
        // STAFF LEVEL FIX: Handle null locationFullName gracefully during sort
        const nameA = a.locationFullName || "";
        const nameB = b.locationFullName || "";
        return nameA.localeCompare(nameB);
      }) || [],
  };

  const isOnHand = (formData) => {
    return (
      formData.status !== InventoryStatus.IN_TRANSIT &&
      formData.status !== InventoryStatus.ARRIVED
    );
  };

  const getQtyLablel = (formData) => {
    return isOnHand(formData) ? "Qty. On Hand" : "Putaway Qty.";
  };

  const renderUnits = () => getUnitName(record.inventoryUOM);

  const openInventoryItem = (jsonRecord) => {
    const jsonObject = JSON.parse(jsonRecord);
    const toPath = `/inventoryItems/${jsonObject.id}/show`;
    deepRedirect(history, toPath, backPath, {
      inventory: record,
    });
  };

  return (
    <Grid container direction="column" spacing={0}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Product Name">
            <TextField source="productName" />
          </Labeled>
          <ProductLinkIcon {...props} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Number">
            <TextField source="lotNumber" />
          </Labeled>
        </Grid>
      </Grid>

      {record.status !== InventoryStatus.STORED &&
        record.status !== InventoryStatus.SHIPPED && (
          <Grid container item direction="row" spacing={1}>
            <Grid item xs={12} md={6}>
              <Labeled label="Quantity Advised">
                <NumberField source="quantityAdvised" />
              </Labeled>
              <Typography>{getUnitName(record.inventoryUOM)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Quantity Received">
                <NumberField source="quantityReceived" />
              </Labeled>
              <ValidateRequiredIcon {...props} />
            </Grid>
          </Grid>
        )}

      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container item direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity On Hand">
              <NumberField source="quantityOnHand" />
            </Labeled>
            <Typography>{getUnitName(record.inventoryUOM)}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {record.quantityMismatch ? (
              <>
                <Labeled label="Quantity Physical">
                  <NumberField source="quantityPhysical" />
                </Labeled>
                <AuditStatusIcon {...props} />
              </>
            ) : null}
          </Grid>
        </Grid>
      )}
      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity Committed">
              <NumberField source="quantityCommitted" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity Available">
              <FunctionField
                source="quantityAvailable"
                render={(record) =>
                  trimDecimal(
                    record.quantityOnHand - record.quantityCommitted,
                    3,
                  )
                }
              />
            </Labeled>
          </Grid>
        </Grid>
      )}
      {showWeight(record?.account, record) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Weight">
              <FunctionField
                render={(record) =>
                  quantityRenderer(
                    record.weight,
                    `${record.weightUOM}/${record.inventoryUOM}`,
                  )
                }
              />
            </Labeled>
          </Grid>
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Type">
            <SelectField source="lotType" choices={LotTypes} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Load Type">
            <SelectField
              source="reloadYardCommodityId"
              choices={yardCommodities}
              optionText="commodity.name"
            />
          </Labeled>
        </Grid>
        <CustomFieldsMobile
          recordFields={record.customFields}
          accountFields={record.account?.inventoryCustomFields}
          width={6}
        />
      </Grid>

      <Grid item container direction="row" spacing={1}>
        <Grid item xs={12}>
          <Typography variant="overline" color="textSecondary">
            Locations:
          </Typography>
          <br />
        </Grid>
        {inventoryItems?.items?.length === 0 && (
          <>
            <Grid item xs={12} md={6}>
              <Labeled label="Default Area">
                <FunctionField
                  render={(record) => getAreaName(record.areaId)}
                />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Default Zone">
                <FunctionField
                  render={(record) => getZoneName(record.areaId, record.zoneId)}
                />
              </Labeled>
            </Grid>
          </>
        )}
        {inventoryItems?.items?.length > 0 && (
          <Grid item xs={12}>
            <ArrayField
              source="items"
              record={inventoryItems}
              basePath="/inventoryItems"
            >
              <Datagrid rowClick={openInventoryItem}>
                <FunctionField
                  source="hasException"
                  label=" "
                  render={exceptionRenderer}
                />
                <FunctionField
                  label="Location"
                  render={(record) =>
                    getLocationFullName(
                      record.location || record.locationFullName,
                    )
                  }
                />
                <NumberField
                  source="quantityOnHand"
                  label={getQtyLablel(record)}
                />
                {isOnHand(record) && (
                  <NumberField
                    source="quantityCommitted"
                    label="Qty. Committed"
                  />
                )}
                <FunctionField label="UOM" render={renderUnits} />
                {record.status !== InventoryStatus.IN_TRANSIT && (
                  <TextField source="unloadedBy" label="Unloaded By" />
                )}
                <ShowButton {...props} state={{ inventory: record }} />
              </Datagrid>
            </ArrayField>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const ShowActions = (props) => (
  <TopToolbar>
    <BackButton {...props} />
  </TopToolbar>
);

export const InventoryShow = (props) => {
  const { record } = useShowController(props);
  const refresh = useRefresh();
  const history = useHistory();

  const reloadYardId = window.appUser?.reloadYardId;

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(reloadYardId && record?.id),
    label: "InventoryShow",
    shouldRefresh: (trackCar) => {
      return shouldRefreshInventoryTrackCar({
        trackCar,
        reloadYardId,
        inboundShipmentId: record?.inboundShipmentId,
        recordId: record?.id,
      });
    },
    onRefresh: () => {
      setTimeout(() => refresh(), 2500);
      setTimeout(() => refresh(), 6000);
    },
  });

  const backPath = getBackPath(props);
  const inbound =
    props.location?.state?.inboundRecord || record?.inboundShipment;

  const exceptions = !record
    ? {}
    : {
        hasException: record.hasException,
        overShipment: record.overShipment,
        overShipmentQuantity: record.overShipmentQuantity,
        overShipmentNote: record.overShipmentNote,
        underShipment: record.underShipment,
        underShipmentQuantity: record.underShipmentQuantity,
        underShipmentNote: record.underShipmentNote,
        isDamaged: record.isDamaged,
        isRecalled: record.isRecalled,
        recallNote: record.recallNote,
        otherException: record.otherException,
        otherExceptionNote: record.otherExceptionNote,
      };

  if (!record?.account || !inbound) return <Loading />;
  return (
    <Show
      {...props}
      title={<ForceTitle title="View Inventory" />}
      actions={<ShowActions {...props} backPath={backPath} />}
      aside={
        <InventoryAside
          {...props}
          history={history}
          backPath={backPath}
          exceptions={exceptions}
        />
      }
    >
      <TabbedShowLayout>
        <Tab label="Inventory Setup">
          <InventorySetupTab {...props} history={history} backPath={backPath} />
        </Tab>
        <Tab label="Notes">
          <NotesTabShow />
        </Tab>
        <Tab label="History Log">
          <HistoryTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
